/**
 * CategoryCombobox
 * Single-select combobox that also allows free-text entry of a new value.
 * Used for SOP category / subcategory inputs (backend find-or-create).
 */

import { useState } from 'react';
import { Check, ChevronsUpDown, PlusCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';

interface CategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  // When set, the dropdown shows only this message instead of the search box/options —
  // e.g. "Please select a category first" for the subcategory picker before a category
  // has been chosen.
  lockedMessage?: string;
}

export function CategoryCombobox({ value, onChange, options, placeholder = 'Select…', disabled, lockedMessage }: CategoryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const trimmed = search.trim();
  const hasExactMatch = options.some((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  const canCreate = trimmed.length > 0 && !hasExactMatch;

  const select = (val: string) => {
    onChange(val);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal text-xs h-10', !value && 'text-muted-foreground')}
        >
          <span className='truncate'>{value || placeholder}</span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[var(--radix-popover-trigger-width)] p-0 max-h-[var(--radix-popover-content-available-height)] overflow-hidden'
        align='start'
      >
        {lockedMessage ? (
          <div className='py-6 text-center text-xs text-muted-foreground'>{lockedMessage}</div>
        ) : (
          <Command>
            <CommandInput placeholder='Search or type to add…' value={search} onValueChange={setSearch} className='text-xs' />
            <CommandList className='max-h-72 overflow-y-auto'>
              {!canCreate && (
                <CommandEmpty className='py-3 text-center text-xs text-muted-foreground'>
                  {trimmed ? 'No matches.' : 'Type to search or add a new one'}
                </CommandEmpty>
              )}
              <CommandGroup>
                {canCreate && (
                  <CommandItem value={trimmed} onSelect={() => select(trimmed)} className='cursor-pointer text-xs'>
                    <PlusCircle className='mr-2 h-4 w-4 text-muted-foreground' />
                    Use &quot;{trimmed}&quot;
                  </CommandItem>
                )}
                {options.map((opt) => (
                  <CommandItem key={opt} value={opt} onSelect={() => select(opt)} className='cursor-pointer text-xs'>
                    <Check className={cn('mr-2 h-4 w-4', value === opt ? 'opacity-100' : 'opacity-0')} />
                    <span className='truncate'>{opt}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
